import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import BoardList from './components/BoardList';
import Board from './components/Board';
import WorkspaceSelector from './components/WorkspaceSelector';
import WorkspaceIndex from './components/WorkspaceIndex';
import AdminPanel from './components/admin/AdminPanel';
import UserSettings from './components/UserSettings';
import AuthPage from './components/auth/AuthPage';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { boardAPI, workspaceAPI, handleAPIError } from './services/api';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Main app component that requires authentication
const AuthenticatedApp = () => {
  const { user, loading: authLoading, isAuthenticated, logout } = useAuth();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [boards, setBoards] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [view, setView] = useState('index'); // 'index', 'boards', 'board', 'admin', or 'settings'
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createBoardTrigger, setCreateBoardTrigger] = useState(null);

  // Load workspaces and boards from API on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadWorkspaces();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentWorkspace && isAuthenticated) {
      loadBoards();
    }
  }, [currentWorkspace, isAuthenticated]);

  // Show loading or redirect to login if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4FC3F7] mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  const loadWorkspaces = async () => {
    try {
      setDataLoading(true);
      setError(null);
      const workspacesData = await workspaceAPI.getWorkspaces();
      setWorkspaces(workspacesData);
      
      // Load all boards across all workspaces for the header dropdown
      const allBoardsData = await boardAPI.getBoards(); // Get all boards without workspace filter
      setAllBoards(allBoardsData);
      
      // If no workspace is selected and workspaces exist, select the first one
      if (workspacesData.length > 0 && !currentWorkspace) {
        setCurrentWorkspace(workspacesData[0]);
      }
    } catch (error) {
      const errorInfo = handleAPIError(error);
      setError(errorInfo.message);
      toast.error(`Failed to load workspaces: ${errorInfo.message}`);
      console.error('Error loading workspaces:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const loadBoards = async () => {
    if (!currentWorkspace) return;
    
    try {
      setDataLoading(true);
      setError(null);
      const boardsData = await workspaceAPI.getWorkspaceBoards(currentWorkspace.id);
      setBoards(boardsData);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      setError(errorInfo.message);
      toast.error(`Failed to load boards: ${errorInfo.message}`);
      console.error('Error loading boards:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSelectBoard = async (board) => {
    try {
      setDataLoading(true);
      // Get full board details from API
      const fullBoard = await boardAPI.getBoard(board.id);
      setCurrentBoard(fullBoard);
      setView('board');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to load board: ${errorInfo.message}`);
      console.error('Error loading board:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleWorkspaceChange = (workspace) => {
    setCurrentWorkspace(workspace);
    setCurrentBoard(null);
    setView('boards');
  };

  const handleWorkspaceSelect = (workspace) => {
    setCurrentWorkspace(workspace);
    setCurrentBoard(null);
    setView('boards');
  };

  const handleCreateWorkspace = async (workspaceData) => {
    try {
      const newWorkspace = await workspaceAPI.createWorkspace(workspaceData);
      setWorkspaces(prev => [...prev, newWorkspace]);
      setCurrentWorkspace(newWorkspace);
      toast.success(`Workspace "${newWorkspace.name}" created successfully!`);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to create workspace: ${errorInfo.message}`);
      console.error('Error creating workspace:', error);
    }
  };

  const handleUpdateWorkspace = async (workspaceId, updateData) => {
    try {
      const updatedWorkspace = await workspaceAPI.updateWorkspace(workspaceId, updateData);
      setWorkspaces(prev => 
        prev.map(workspace => 
          workspace.id === workspaceId ? updatedWorkspace : workspace
        )
      );
      
      if (currentWorkspace && currentWorkspace.id === workspaceId) {
        setCurrentWorkspace(updatedWorkspace);
      }
      
      toast.success(`Workspace "${updatedWorkspace.name}" updated successfully!`);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to update workspace: ${errorInfo.message}`);
      console.error('Error updating workspace:', error);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    try {
      await workspaceAPI.deleteWorkspace(workspaceId);
      setWorkspaces(prev => prev.filter(workspace => workspace.id !== workspaceId));
      
      // If deleted workspace was current, select another or clear
      if (currentWorkspace && currentWorkspace.id === workspaceId) {
        const remainingWorkspaces = workspaces.filter(w => w.id !== workspaceId);
        setCurrentWorkspace(remainingWorkspaces.length > 0 ? remainingWorkspaces[0] : null);
        setBoards([]);
      }
      
      toast.success('Workspace deleted successfully!');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to delete workspace: ${errorInfo.message}`);
      console.error('Error deleting workspace:', error);
    }
  };

  const handleCreateBoard = async (newBoardData) => {
    if (!currentWorkspace) {
      toast.error('Please select a workspace first');
      return;
    }
    
    try {
      const boardData = { ...newBoardData, workspace_id: currentWorkspace.id };
      const newBoard = await boardAPI.createBoard(boardData);
      setBoards(prev => [...prev, newBoard]);
      setAllBoards(prev => [...prev, newBoard]); // Also update allBoards
      toast.success(`Board "${newBoard.title}" created successfully!`);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to create board: ${errorInfo.message}`);
      console.error('Error creating board:', error);
    }
  };

  const handleBoardSelectFromHeader = async (board) => {
    try {
      setDataLoading(true);
      // Switch to the board's workspace if different
      if (board.workspace_id !== currentWorkspace?.id) {
        const boardWorkspace = workspaces.find(w => w.id === board.workspace_id);
        if (boardWorkspace) {
          setCurrentWorkspace(boardWorkspace);
        }
      }
      
      // Get full board details from API
      const fullBoard = await boardAPI.getBoard(board.id);
      setCurrentBoard(fullBoard);
      setView('board');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to load board: ${errorInfo.message}`);
      console.error('Error loading board:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleUpdateBoard = async (updatedBoardData) => {
    try {
      const updatedBoard = await boardAPI.updateBoard(updatedBoardData.id, updatedBoardData);
      
      // Update boards list
      setBoards(prev => 
        prev.map(board => 
          board.id === updatedBoard.id ? updatedBoard : board
        )
      );
      
      // Update current board if it's the same
      if (currentBoard && currentBoard.id === updatedBoard.id) {
        setCurrentBoard(updatedBoard);
      }
      
      return updatedBoard;
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to update board: ${errorInfo.message}`);
      console.error('Error updating board:', error);
      throw error;
    }
  };

  const handleDeleteBoard = async (boardId) => {
    try {
      await boardAPI.deleteBoard(boardId);
      
      // Remove board from list
      setBoards(prev => prev.filter(board => board.id !== boardId));
      
      // If the deleted board was currently open, go back to boards view
      if (currentBoard && currentBoard.id === boardId) {
        setCurrentBoard(null);
        setView('boards');
      }
      
      toast.success('Board deleted successfully!');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to delete board: ${errorInfo.message}`);
      console.error('Error deleting board:', error);
    }
  };

  const handleBackToBoards = () => {
    setCurrentBoard(null);
    setView('boards');
  };

  const handleQuickCreateBoard = () => {
    if (!currentWorkspace) {
      // If no workspace selected, go to index
      setView('index');
      return;
    }
    // Navigate to boards view and trigger board creation
    setView('boards');
    // We'll pass a signal to BoardList to open the create dialog
    setCreateBoardTrigger(Date.now());
  };

  const handleGoHome = () => {
    setView('index');
    setCurrentWorkspace(null);
    setCurrentBoard(null);
  };

  const handleAdminPanel = () => {
    setView('admin');
  };

  const handleSettings = () => {
    setView('settings');
  };

  const handleBackFromSettings = () => {
    setView('index');
  };

  if (dataLoading && workspaces.length === 0) {
    return (
      <div className="App min-h-screen bg-gray-50">
        <Header 
          currentBoard={currentBoard}
          currentWorkspace={currentWorkspace}
          workspaces={workspaces}
          allBoards={allBoards}
          onWorkspaceChange={handleWorkspaceChange}
          onBoardSelect={handleBoardSelectFromHeader}
          onGoHome={handleGoHome}
          user={user}
          onLogout={logout}
          onAdminPanel={handleAdminPanel}
          onSettings={handleSettings}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading workspaces...</p>
          </div>
        </div>
        <Toaster 
        position="top-right" 
        closeButton={true}
        richColors={true}
        expand={false}
        duration={4000}
      />
      </div>
    );
  }

  if (error && workspaces.length === 0) {
    return (
      <div className="App min-h-screen bg-gray-50">
        <Header 
          currentBoard={currentBoard}
          currentWorkspace={currentWorkspace}
          workspaces={workspaces}
          allBoards={allBoards}
          onWorkspaceChange={handleWorkspaceChange}
          onBoardSelect={handleBoardSelectFromHeader}
          onGoHome={handleGoHome}
          user={user}
          onLogout={logout}
          onAdminPanel={handleAdminPanel}
          onSettings={handleSettings}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Workspaces</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadWorkspaces}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
        <Toaster 
        position="top-right" 
        closeButton={true}
        richColors={true}
        expand={false}
        duration={4000}
      />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gray-50">
      <Header 
        currentBoard={currentBoard}
        currentWorkspace={currentWorkspace}
        workspaces={workspaces}
        allBoards={allBoards}
        onWorkspaceChange={handleWorkspaceChange}
        onBoardSelect={handleBoardSelectFromHeader}
        onGoHome={handleGoHome}
        user={user}
        onLogout={logout}
        onAdminPanel={handleAdminPanel}
        onSettings={handleSettings}
      />
      
      {view === 'index' ? (
        <WorkspaceIndex
          workspaces={workspaces}
          allBoards={allBoards}
          currentUser={user}
          onWorkspaceSelect={handleWorkspaceSelect}
          onCreateWorkspace={handleCreateWorkspace}
          onUpdateWorkspace={handleUpdateWorkspace}
          onDeleteWorkspace={handleDeleteWorkspace}
        />
      ) : view === 'boards' ? (
        <div>
          {workspaces.length > 0 && (
            <div className="border-b bg-white px-6 py-4">
              <WorkspaceSelector
                workspaces={workspaces}
                currentWorkspace={currentWorkspace}
                onWorkspaceChange={handleWorkspaceChange}
                onCreateWorkspace={handleCreateWorkspace}
                onUpdateWorkspace={handleUpdateWorkspace}
                onDeleteWorkspace={handleDeleteWorkspace}
              />
            </div>
          )}
          
          {currentWorkspace ? (
            <BoardList
              boards={boards}
              onSelectBoard={handleSelectBoard}
              onCreateBoard={handleCreateBoard}
              onUpdateBoard={handleUpdateBoard}
              onDeleteBoard={handleDeleteBoard}
              loading={dataLoading}
              workspace={currentWorkspace}
              createBoardTrigger={createBoardTrigger}
            />
          ) : (
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">No workspaces found</h2>
                <p className="text-gray-600 mb-6">Create your first workspace to start organizing your boards</p>
                <WorkspaceSelector
                  workspaces={workspaces}
                  currentWorkspace={currentWorkspace}
                  onWorkspaceChange={handleWorkspaceChange}
                  onCreateWorkspace={handleCreateWorkspace}
                  onUpdateWorkspace={handleUpdateWorkspace}
                  onDeleteWorkspace={handleDeleteWorkspace}
                />
              </div>
            </div>
          )}
        </div>
      ) : view === 'admin' ? (
        <AdminPanel />
      ) : view === 'settings' ? (
        <UserSettings 
          currentUser={user} 
          onBack={handleBackFromSettings}
          onUserUpdate={(updatedUser) => {
            // This will be handled by refreshing user data from API
            console.log('User updated:', updatedUser);
          }}
        />
      ) : currentBoard ? (
        <div>
          <div className="bg-white border-b px-4 py-2">
            <button
              onClick={handleBackToBoards}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              ← Back to boards
            </button>
          </div>
          <Board
            board={currentBoard}
            onUpdateBoard={handleUpdateBoard}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No board selected</h2>
            <button
              onClick={handleBackToBoards}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Go back to boards
            </button>
          </div>
        </div>
      )}
      
      <Toaster 
        position="top-right" 
        closeButton={true}
        richColors={true}
        expand={false}
        duration={4000}
      />
    </div>
  );
};

// Main App component with Authentication Provider
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;